/**
 * 等保三级「三员分立」前端落地引导（纯函数，无 UI 依赖）
 *
 * 权威源：backend/shared/kernel/role_guard.py 的 THREE_ROLE_ADMINS——
 * 系统管理员（sys_admin）/ 安全管理员（sec_admin）/ 审计管理员（audit_admin）
 * 为平行于业务面的平台管理角色：require_roles 的等级继承明确排除三员，
 * 三员访问临床业务端点必达 403「权限不足」（如科室/患者/病历/质检配置）。
 *
 * 因此前端在路由守卫层把三员账号统一引导至平台角色说明页，
 * 避免其登录后进入临床工作区引发级联 403 弹窗与空壳界面。
 */

/** 与 backend/shared/kernel/role_guard.py 的 THREE_ROLE_ADMINS 保持同步 */
export const THREE_ROLE_ADMINS: ReadonlySet<string> = new Set([
  "sys_admin",
  "sec_admin",
  "audit_admin",
]);

/** 三员账号的登录落地页（平台角色说明，零业务 API 调用） */
export const PLATFORM_ROLE_NOTICE_PATH = "/platform-role-notice";

/** 临床工作区首页（反向防呆：非三员误入说明页时回跳） */
const MEDICAL_HOME_PATH = "/medical";

/** 角色显示名（与控制面/等保验收口径一致） */
const ROLE_LABELS: Record<string, string> = {
  sys_admin: "系统管理员",
  sec_admin: "安全管理员",
  audit_admin: "审计管理员",
};

interface ThreeRoleUserLike {
  role?: string | null;
  roles?: readonly string[] | null;
}

/**
 * 判定用户是否持有三员角色（role 与 roles 任一命中即 true；与后端
 * iter_user_roles、前端 managedAccount.isPlatformManagedUser 同语义）。
 * user 未就绪（null / 字段未下发）时 fail-open 返回 false，
 * 由后端 403 兜底，避免误伤。
 */
export function isThreeRoleAdmin(
  user: ThreeRoleUserLike | null | undefined,
): boolean {
  if (!user) return false;
  const codes: Array<string | null | undefined> = [
    user.role,
    ...(user.roles ?? []),
  ];
  return codes.some((code) => !!code && THREE_ROLE_ADMINS.has(code));
}

/** 提取三员角色的中文显示名；非三员或未就绪时返回通用称谓 */
export function threeRoleLabel(
  user: ThreeRoleUserLike | null | undefined,
): string {
  const codes: Array<string | null | undefined> = [
    ...(user?.roles ?? []),
    user?.role,
  ];
  for (const code of codes) {
    if (code && ROLE_LABELS[code]) return ROLE_LABELS[code];
  }
  return "平台管理角色";
}

/**
 * 纯函数：计算三员账号的导航重定向目标（null = 放行）。
 *
 * 规则：
 * - 目标已是说明页：三员放行；非三员反向防呆回临床首页；
 * - 三员访问白名单页（登录/404/下载页等）：放行，不干扰登录页自身逻辑；
 * - 三员访问其余任何页面：重定向到说明页。
 */
export function resolveThreeRoleRedirect(
  user: ThreeRoleUserLike | null | undefined,
  toPath: string,
  isWhiteListed: boolean,
): string | null {
  if (toPath === PLATFORM_ROLE_NOTICE_PATH) {
    return isThreeRoleAdmin(user) ? null : MEDICAL_HOME_PATH;
  }
  if (!isThreeRoleAdmin(user)) return null;
  if (isWhiteListed) return null;
  return PLATFORM_ROLE_NOTICE_PATH;
}
